/**
 * ConfigApiClient
 * 
 * Handles application configuration and settings
 */

import { BaseApiClient } from './BaseApiClient';

export class ConfigApiClient extends BaseApiClient {
  /**
   * Get Stripe configuration
   */
  async getStripeConfig(): Promise<{
    publishableKey: string;
    environment: string;
    customerId?: string;
  }> {
    return this.get<{
      publishableKey: string;
      environment: string;
      customerId?: string;
    }>('/config/stripe');
  }

  /**
   * Get Plaid configuration
   */
  async getPlaidConfig(): Promise<{
    clientId: string;
    environment: string;
    enabled: boolean;
    products: string[];
    countryCodes: string[];
  }> {
    return this.get<{
      clientId: string;
      environment: string;
      enabled: boolean;
      products: string[];
      countryCodes: string[];
    }>('/config/plaid');
  }

  /**
   * Get app configuration settings
   */
  async getAppConfig(appName: string = 'vaiu-app'): Promise<{
    appName: string;
    ui: {
      showPrepaidCardOption: boolean;
      defaultCurrency: string;
      enableReferralTokens: boolean;
      maxTokenAmount: number;
      minTokenAmount: number;
      supportedCurrencies: string[];
      cardIssuanceRules: Record<string, { min: number; max: number }>;
    };
    features: {
      enableInvoiceTokens: boolean;
      enableMerchantProfiles: boolean;
      enableChatFeatures: boolean;
      enableAnalytics: boolean;
      enableNotifications: boolean;
      nfcPaymentMethod: 'react' | 'native';
    };
    limits: {
      dailyTokenLimit: number;
      monthlyTransactionLimit: number;
      maxActiveTokens: number;
      tokenExpiryDays: number;
    };
    integrations: {
      stripeEnabled: boolean;
      paypalEnabled: boolean;
      plaidEnabled: boolean;
      boPaymentsEnabled: boolean;
    };
    branding: {
      appName: string;
      primaryColor: string;
      logoUrl: string;
      supportEmail: string;
      companyName: string;
    };
    security: {
      requireEmailVerification: boolean;
      enableTwoFactorAuth: boolean;
      sessionTimeoutMinutes: number;
      maxLoginAttempts: number;
    };
  }> {
    return this.get(`/config/app/${appName}`);
  }

  /**
   * Update app configuration settings (admin only)
   */
  async updateAppConfig(config: {
    ui?: {
      showPrepaidCardOption?: boolean;
      defaultCurrency?: string;
      enableReferralTokens?: boolean;
      maxTokenAmount?: number;
      minTokenAmount?: number;
      supportedCurrencies?: string[];
      cardIssuanceRules?: Record<string, { min: number; max: number }>;
    };
    features?: {
      enableInvoiceTokens?: boolean;
      enableMerchantProfiles?: boolean;
      enableChatFeatures?: boolean;
      enableAnalytics?: boolean;
      enableNotifications?: boolean;
      nfcPaymentMethod?: 'react' | 'native';
    };
    limits?: {
      dailyTokenLimit?: number;
      monthlyTransactionLimit?: number;
      maxActiveTokens?: number;
      tokenExpiryDays?: number;
    };
    integrations?: {
      stripeEnabled?: boolean;
      paypalEnabled?: boolean;
      plaidEnabled?: boolean;
      boPaymentsEnabled?: boolean;
    };
    branding?: {
      appName?: string;
      primaryColor?: string;
      logoUrl?: string;
      supportEmail?: string;
      companyName?: string;
    };
    security?: {
      requireEmailVerification?: boolean;
      enableTwoFactorAuth?: boolean;
      sessionTimeoutMinutes?: number;
      maxLoginAttempts?: number;
    };
  }, appName: string = 'vaiu-app'): Promise<{ success: boolean; message: string }> {
    return this.put(`/config/app/${appName}`, config);
  }

  /**
   * Get specific configuration section
   */
  async getConfigSection(section: 'ui' | 'features' | 'limits' | 'integrations' | 'branding' | 'security', appName: string = 'vaiu-app'): Promise<any> {
    return this.get(`/config/app/${appName}/${section}`);
  }

  /**
   * Update specific configuration section
   */
  async updateConfigSection(section: 'ui' | 'features' | 'limits' | 'integrations' | 'branding' | 'security', config: any, appName: string = 'vaiu-app'): Promise<{ success: boolean; message: string }> {
    return this.put(`/config/app/${appName}/${section}`, config);
  }

  /**
   * Get list of all configured apps (admin only)
   */
  async getConfiguredApps(): Promise<{
    apps: Array<{
      appName: string;
      displayName: string;
      lastUpdated: string;
      version: string;
      description?: string;
    }>;
  }> {
    return this.get('/config/apps');
  }

  /**
   * Create new app configuration (admin only)
   */
  async createAppConfig(appName: string, displayName: string, description?: string): Promise<{ success: boolean; message: string }> {
    return this.post('/config/apps', { appName, displayName, description });
  }

  /**
   * Delete app configuration (admin only)
   */
  async deleteAppConfig(appName: string): Promise<{ success: boolean; message: string }> {
    return this.delete(`/config/app/${appName}`);
  }

  /**
   * Get environment information
   */
  async getEnvironmentInfo(): Promise<{
    environment: string;
    version: string;
    buildDate: string;
    features: string[];
  }> {
    return this.get('/config/environment');
  }

  /**
   * Get system status
   */
  async getSystemStatus(): Promise<{
    status: 'healthy' | 'degraded' | 'offline';
    services: Record<string, {
      status: 'up' | 'down' | 'degraded';
      lastCheck: string;
      responseTime?: number;
    }>;
    uptime: number;
  }> {
    return this.get('/config/system-status');
  }
} 